import type { CSSProperties } from 'react';

/**
 * Field value typography: --text-body-lg / regular / primary.
 * Sits above labels (--text-small) and below section titles (--text-body-sm).
 *
 * Shared across the drawer forms (agenda, client, visit, launch) so the
 * input chrome stays visually identical across features.
 */
export const drawerInputStyle: CSSProperties = {
  width: '100%',
  height: '2.5rem',
  padding: 'var(--space-2) var(--space-3)',
  border: '1px solid var(--border-subtle)',
  borderRadius: 'var(--radius-md)',
  background: 'var(--surface-tertiary)',
  color: 'var(--text-primary)',
  fontSize: 'var(--text-body-lg)',
  fontWeight: 'var(--font-regular)',
  lineHeight: 'var(--leading-normal)',
  fontFamily: 'var(--font-family)',
  outline: 'none',
  boxSizing: 'border-box',
  transition: 'var(--transition-standard)',
};

/**
 * Field label typography: --text-small / medium / tertiary.
 * Quietest text in the form hierarchy (below section eyebrows).
 */
export const drawerLabelStyle: CSSProperties = {
  fontSize: 'var(--text-small)',
  fontWeight: 'var(--font-medium)',
  lineHeight: 'var(--leading-snug)',
  letterSpacing: 'var(--tracking-wide)',
  color: 'var(--text-tertiary)',
  marginBottom: 'var(--space-1)',
  display: 'block',
  fontFamily: 'var(--font-family)',
};

/** Validation error typography: --text-small / regular / error. */
export const drawerErrorStyle: CSSProperties = {
  fontSize: 'var(--text-small)',
  fontWeight: 'var(--font-regular)',
  lineHeight: 'var(--leading-snug)',
  color: 'var(--status-error)',
  marginTop: 'var(--space-1)',
  fontFamily: 'var(--font-family)',
};

/** Multi-line notes field — same tokens as inputs, free height. */
export const drawerTextareaStyle: CSSProperties = {
  ...drawerInputStyle,
  height: 'auto',
  minHeight: '4.5rem',
  resize: 'vertical',
};

/** Left padding when a currency prefix sits inside the input. */
export const drawerCurrencyInputPadding: CSSProperties = {
  paddingLeft: 'calc(var(--space-8) + var(--space-1))',
};

/** Absolute currency prefix (R$) inside price inputs. */
export const drawerCurrencyPrefixStyle: CSSProperties = {
  position: 'absolute',
  left: 'var(--space-3)',
  top: '50%',
  transform: 'translateY(-50%)',
  fontSize: 'var(--text-small)',
  color: 'var(--text-tertiary)',
  fontFamily: 'var(--font-family)',
};
